import React, { useEffect, useMemo, useState } from "react";
import {
  buildFramesFromBackend,
  connectWebSerial,
  getWebSerialStatus,
  onWebSerialStatus,
  sendSemanticConfigViaWebSerial,
} from "../services/webSerial";
import {
  EMULATOR_CATALOG,
  OPERATION_OPTIONS,
  encodeThresholdPair,
  hexByte,
} from "../data/emulatorCatalog";
import FrameInspector from "./FrameInspector";
import "./OperationalViews.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:3001";
const EMPTY_VARIABLES = Object.freeze([]);

function initialConfiguration(variables) {
  return Object.fromEntries(
    variables.map((variable) => [
      variable.key,
      {
        include: true,
        operation: "NONE",
        operand: "0",
        thresholdLow: String(variable.min),
        thresholdHigh: String(variable.max),
      },
    ])
  );
}

export default function EmulatorConfigView({ subsystem, title, icon, description }) {
  const variables = EMULATOR_CATALOG[subsystem] || EMPTY_VARIABLES;
  const [selectedKey, setSelectedKey] = useState(variables[0]?.key || "");
  const [configs, setConfigs] = useState(() => initialConfiguration(variables));
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [webStatus, setWebStatus] = useState(getWebSerialStatus());

  useEffect(() => onWebSerialStatus(setWebStatus), []);

  const selectedVariable = variables.find((item) => item.key === selectedKey) || variables[0];
  const selectedConfig = configs[selectedVariable?.key] || {};
  const operation = OPERATION_OPTIONS.find((item) => item.value === selectedConfig.operation);

  const includedCount = useMemo(
    () => variables.filter((variable) => configs[variable.key]?.include).length,
    [configs, variables]
  );

  const updateConfig = (key, field, value) => {
    setConfigs((current) => ({
      ...current,
      [key]: { ...current[key], [field]: value },
    }));
    setPreview(null);
  };

  const buildRequest = () => {
    const devices = variables
      .filter((variable) => configs[variable.key]?.include)
      .map((variable) => {
        const config = configs[variable.key];
        const variableOperation = OPERATION_OPTIONS.find(
          (item) => item.value === config.operation
        );
        const thresholds = encodeThresholdPair(
          variable,
          config.thresholdLow,
          config.thresholdHigh
        );
        const operand = Number(config.operand || 0);
        if (!Number.isInteger(operand) || operand < 0 || operand > 0xffff) {
          throw new Error(`${variable.name}: el operando debe ser entero entre 0 y 65535.`);
        }
        return {
          key: `variable-${variable.id}`,
          itemId: variable.id,
          name: variable.name,
          config: {
            itemId: variable.id,
            emulator: {
              operation: config.operation,
              operand: variableOperation?.needsOperand ? operand : 0,
              thresholdLow: thresholds.low,
              thresholdHigh: thresholds.high,
            },
          },
        };
      });

    if (!devices.length) throw new Error("Selecciona al menos una variable para generar las tramas.");

    return {
      subsystem,
      profile: "EMULATOR",
      operation: "configure-emulator",
      apply: true,
      persist: false,
      devices,
    };
  };

  const generatePreview = async () => {
    try {
      setBusy("preview");
      const result = await buildFramesFromBackend(API_BASE, buildRequest());
      setPreview(result);
      setMessage(`${result.framesCount} tramas generadas para ${includedCount} variables.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy("");
    }
  };

  const connect = async () => {
    try {
      setBusy("connect");
      await connectWebSerial({ baudRate: 115200 });
      setMessage("Tarjeta local conectada por Web Serial.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    try {
      setBusy("send");
      const result = await sendSemanticConfigViaWebSerial(API_BASE, buildRequest());
      setPreview(result);
      setMessage(
        result.webSerial?.sent
          ? `${result.framesCount} tramas confirmadas por ACK.`
          : `${result.framesCount} tramas generadas. Conecta la tarjeta para transmitirlas.`
      );
    } catch (error) {
      setMessage(`No se completó el envío: ${error.message}`);
    } finally {
      setBusy("");
    }
  };

  if (!selectedVariable) return <p>No hay variables definidas para {subsystem}.</p>;

  return (
    <div className="emulator-view">
      <section className="ops-hero">
        <div>
          <p className="ops-eyebrow">Generadores definitivos del PIC</p>
          <h3>{icon} {title}</h3>
          <p>{description}</p>
        </div>
        <div className={`ops-link-state ${webStatus.connected ? "is-connected" : ""}`}>
          <span />
          {webStatus.connected ? "Web Serial conectado" : "Sin tarjeta local"}
        </div>
      </section>

      <p className="ops-note" style={{ borderColor: "rgba(250, 204, 21, .35)", color: "#fde68a" }}>
        Firmware pendiente: BEGIN_CONFIG, WRITE_PARAM y APPLY_CONFIG se conservan como contrato propuesto y para pruebas de comando inválido. El preview es válido según la GUI, pero el esclavo todavía no aplica esta configuración.
      </p>

      <div className="emulator-layout">
        <section className="ops-card emulator-variable-list">
          <div className="ops-section-heading">
            <div>
              <strong>Variables programadas</strong>
              <small>{includedCount} incluidas en el envío</small>
            </div>
          </div>
          <div className="emulator-variable-grid">
            {variables.map((variable) => {
              const config = configs[variable.key];
              const active = variable.key === selectedVariable.key;
              return (
                <div className={`emulator-variable ${active ? "is-active" : ""}`} key={variable.key}>
                  <button type="button" onClick={() => setSelectedKey(variable.key)}>
                    <strong>{variable.name}</strong>
                    <small>{hexByte(variable.id)} · {variable.min} a {variable.max} {variable.unit}</small>
                  </button>
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(config?.include)}
                      onChange={(event) => updateConfig(variable.key, "include", event.target.checked)}
                    />
                    Incluir
                  </label>
                </div>
              );
            })}
          </div>
        </section>

        <section className="ops-card emulator-editor">
          <div className="ops-section-heading">
            <div>
              <strong>{selectedVariable.name}</strong>
              <small>VARIABLE_ID {hexByte(selectedVariable.id)} · Paso {selectedVariable.step} {selectedVariable.unit}</small>
            </div>
            <span className="ops-badge">{selectedVariable.conversion}</span>
          </div>

          <div className="emulator-form-grid">
            <label>
              Operación
              <select
                value={selectedConfig.operation}
                onChange={(event) => updateConfig(selectedVariable.key, "operation", event.target.value)}
              >
                {OPERATION_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </label>
            <label>
              Operando bruto
              <input
                type="number"
                min="0"
                max="65535"
                step="1"
                value={selectedConfig.operand}
                disabled={!operation?.needsOperand}
                onChange={(event) => updateConfig(selectedVariable.key, "operand", event.target.value)}
              />
            </label>
            <label>
              Umbral bajo real ({selectedVariable.unit})
              <input
                type="number"
                min={selectedVariable.min}
                max={selectedVariable.max}
                step={selectedVariable.step}
                value={selectedConfig.thresholdLow}
                onChange={(event) => updateConfig(selectedVariable.key, "thresholdLow", event.target.value)}
              />
            </label>
            <label>
              Umbral alto real ({selectedVariable.unit})
              <input
                type="number"
                min={selectedVariable.min}
                max={selectedVariable.max}
                step={selectedVariable.step}
                value={selectedConfig.thresholdHigh}
                onChange={(event) => updateConfig(selectedVariable.key, "thresholdHigh", event.target.value)}
              />
            </label>
          </div>

          <div className="ops-note">
            La GUI convierte los umbrales al valor sin signo que compara el PIC. Un umbral codificado como cero queda deshabilitado por el firmware.
          </div>
        </section>
      </div>

      <section className="ops-card">
        <div className="ops-actions">
          <button type="button" onClick={generatePreview} disabled={Boolean(busy)}>
            {busy === "preview" ? "Generando…" : "Previsualizar tramas"}
          </button>
          <button type="button" onClick={connect} disabled={Boolean(busy) || webStatus.connected}>
            {webStatus.connected ? "Tarjeta conectada" : "Conectar tarjeta"}
          </button>
          <button className="is-primary" type="button" onClick={send} disabled={Boolean(busy)}>
            {busy === "send" ? "Enviando…" : `Enviar ${title}`}
          </button>
        </div>
        {message && <p className="ops-message">{message}</p>}
      </section>

      {preview?.frames?.length > 0 && (
        <section className="ops-card">
          <div className="ops-section-heading">
            <div>
              <strong>Preview UART</strong>
              <small>Comandos reservados: BEGIN_CONFIG, WRITE_PARAM y APPLY_CONFIG</small>
            </div>
            <span className="ops-badge">{preview.framesCount} tramas</span>
          </div>
          <div className="ops-frame-list">
            {preview.frames.map((frame) => (
              <FrameInspector
                key={`${frame.seq}-${frame.label}`}
                hex={frame.frameHex}
                direction="TX"
                label={`SEQ=${String(frame.seq).padStart(3, "0")} · ${frame.label}`}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
